/**
 * Object annotation, ARCHITECTURE.md §4.3.3.
 *
 * Object points are percent of the object's raster box (SVG intrinsic box or
 * PNG). `affordances` and `lints` are DERIVED on save.
 */
import { z } from 'zod';
import { Measured, PctBox } from './character';
import { envelope, Id, Lint, Pt } from './common';

export const ObjectFlags = z.strictObject({
  holdable: z.boolean(),
  sittable: z.boolean(),
  sleepable: z.boolean().optional(),
  wearable: z.boolean().optional(),
  container: z.boolean().optional(),
  openable: z.boolean().optional(),
  pushable: z.boolean().optional(),
  edible: z.boolean().optional(),
  throwable: z.boolean().optional(),
  decor: z.boolean().optional(),
});
export type ObjectFlags = z.infer<typeof ObjectFlags>;

export const ObjectBody = z.strictObject({
  slug: Id,
  category: z.string(),
  display_name: z.string().min(1),
  description: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  /** Raster box the points refer to. */
  source: z.strictObject({
    key: Id,
    format: z.enum(['svg', 'png']),
    box: z.strictObject({ w: z.number().positive(), h: z.number().positive() }),
  }),
  size_cm: z.strictObject({ height: Measured, width: Measured.optional() }),
  /** vocab(surface).key: floor | table | seat | bed | shelf | wall | ceiling | water | sky | hand | sill | hook */
  rest_surface: Id,
  /** Point that sits on the surface; default by surface (floor [50,100], ceiling [50,0], sky/wall [50,50]). */
  rest_point: Pt,
  /** Where a hand holds it; default centre. */
  grip_point: Pt.optional(),
  /** Wall/ceiling objects. */
  attach_point: Pt.optional(),
  bbox: PctBox.optional(),
  flags: ObjectFlags,
  /** world = depth-scaled; fixed = UI-ish (stars, icons). */
  scale_policy: z.enum(['world', 'fixed']),
  /** 'pillow' prefers ['bed']. */
  prefers_anchor_kinds: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  /** DERIVED: hold, sit, sleep, wear, open, push, eat, throw, place:<rest_surface>. */
  affordances: z.array(z.string()),
  lints: z.array(Lint),
});
export type ObjectBody = z.infer<typeof ObjectBody>;

export const ObjectAnnotation = envelope('object', ObjectBody).meta({
  id: 'ObjectAnnotation',
  title: 'Object annotation',
  description: 'Annotation document for a prop (story-studio.object/1).',
});
export type ObjectAnnotation = z.infer<typeof ObjectAnnotation>;
